import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import AdminStaffEditModal from './AdminStaffEditModal';

const PAGE_SIZE = 15;

const EMPTY_FILTERS = {
  username: '',
  realName: '',
  mobilePhone: '',
};

const TABLE_COLUMNS = [
  '帳號',
  '姓名',
  '手機號碼',
  '用戶角色',
  'IP白名單驗證',
  '後台處理人ID',
  '創建人',
  '創建時間',
  '操作',
];

function buildPageParams(pageNo, filters) {
  return {
    pageNo,
    pageSize: PAGE_SIZE,
    ...(filters.username?.trim() ? { username: filters.username.trim() } : {}),
    ...(filters.realName?.trim() ? { realName: filters.realName.trim() } : {}),
    ...(filters.mobilePhone?.trim() ? { mobilePhone: filters.mobilePhone.trim() } : {}),
  };
}

function formatIpValidation(value) {
  return value ? '是' : '否';
}

export default function PeopleManagePage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  async function loadRoles() {
    setRolesLoading(true);
    try {
      const data = await adminApi.adminRolePage({ pageNo: 1, pageSize: 500 });
      setRoles(data?.records || []);
    } catch {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.adminStaffPage(buildPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(payload, isEdit) {
    setSubmitting(true);
    setError('');
    try {
      if (isEdit) {
        await adminApi.updateAdminStaff(payload);
        setMessage('已更新');
      } else {
        await adminApi.createAdminStaff(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.removeAdminStaff([deleteTarget.id]);
      setDeleteTarget(null);
      setMessage('已刪除');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  async function handleConfirmResetPassword() {
    if (!resetTarget?.id) {
      return;
    }
    setResetting(true);
    setError('');
    try {
      await adminApi.resetAdminStaffPassword({ id: resetTarget.id });
      setResetTarget(null);
      setMessage('密碼已重置為 123456');
    } catch (err) {
      setError(err.message || '密碼重置失敗');
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>人員管理</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          新增
        </button>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="staffFilterUsername">帳號</label>
          <input
            id="staffFilterUsername"
            type="text"
            value={filters.username}
            onChange={(e) => updateFilter('username', e.target.value)}
            placeholder="請輸入帳號"
          />
        </div>
        <div className="form-field">
          <label htmlFor="staffFilterRealName">姓名</label>
          <input
            id="staffFilterRealName"
            type="text"
            value={filters.realName}
            onChange={(e) => updateFilter('realName', e.target.value)}
            placeholder="請輸入姓名"
          />
        </div>
        <div className="form-field">
          <label htmlFor="staffFilterMobile">手機號碼</label>
          <input
            id="staffFilterMobile"
            type="text"
            value={filters.mobilePhone}
            onChange={(e) => updateFilter('mobilePhone', e.target.value)}
            placeholder="請輸入手機號碼"
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && <p className="page-desc">共 {total} 筆</p>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{row.username}</td>
                  <td>{row.realName || '—'}</td>
                  <td>{row.mobilePhone || '—'}</td>
                  <td>{row.roleName || '—'}</td>
                  <td>{formatIpValidation(row.enableIpValidation)}</td>
                  <td>{row.id}</td>
                  <td>{row.creatorName || '—'}</td>
                  <td>{row.createTime ? formatDateTime(row.createTime) : '—'}</td>
                  <td>
                    <div className="table-actions table-actions--stacked">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => openEdit(row)}
                      >
                        修改
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDeleteTarget(row)}
                      >
                        刪除
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setResetTarget(row)}
                      >
                        密碼重置
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <AdminStaffEditModal
        open={editOpen}
        row={editRow}
        roles={roles}
        rolesLoading={rolesLoading}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
          }
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除人員"
        message={deleteTarget ? `確定刪除帳號「${deleteTarget.username}」？` : ''}
        confirmLabel={deleting ? '刪除中…' : '確認刪除'}
        confirming={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={Boolean(resetTarget)}
        title="密碼重置"
        message={
          resetTarget
            ? `確定將「${resetTarget.username}」的密碼重置為預設值 123456？`
            : ''
        }
        confirmLabel={resetting ? '處理中…' : '確認重置'}
        confirming={resetting}
        onClose={() => !resetting && setResetTarget(null)}
        onConfirm={handleConfirmResetPassword}
      />
    </div>
  );
}
